import { Heart, Instagram, Github, ArrowRight, Sparkles, Triangle, Circle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Footer() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const year = new Date().getFullYear();

    return (
        <footer className="w-full border-t-4 border-border bg-foreground text-background py-6 sm:py-10 px-4 sm:px-6 mt-auto relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-4 right-8 w-16 h-16 sm:w-20 sm:h-20 border-2 border-background/10 rotate-12 -z-0 opacity-30 hidden sm:block" />
            <div className="absolute bottom-6 left-6 w-10 h-10 sm:w-14 sm:h-14 border-2 border-background/10 rounded-full -z-0 opacity-20 hidden sm:block" />
            <div className="absolute top-1/2 right-1/4 -z-0 opacity-10 hidden md:flex items-center justify-center">
                <Triangle size={40} className="text-background/20" />
            </div>
            <div className="absolute bottom-1/3 left-1/3 -z-0 opacity-10 hidden md:flex items-center justify-center">
                <Circle size={30} className="text-background/20" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Main Footer Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-10">

                    {/* Brand Column */}
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center bg-accent text-accent-foreground font-black text-xs -rotate-3 neo-border border-background">
                                AL
                            </div>
                            <span className="text-lg sm:text-xl font-black tracking-tighter uppercase">AadishLabs</span>
                        </div>
                        <p className="text-background/60 font-medium leading-relaxed text-xs sm:text-sm max-w-xs">
                            Personalized virtual gifts, micro-sites, and videos for the people you love.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div className="flex flex-col gap-3">
                        <h3 className="font-black uppercase tracking-widest text-xs text-background/40">Quick Links</h3>
                        <div className="flex flex-row sm:flex-col gap-3 sm:gap-2 flex-wrap">
                            <button onClick={() => navigate('/')} className="text-left font-bold uppercase tracking-wider text-xs hover:text-primary transition-colors w-fit">
                                Home
                            </button>
                            <button onClick={() => navigate('/about')} className="text-left font-bold uppercase tracking-wider text-xs hover:text-accent transition-colors w-fit">
                                About Us
                            </button>
                            <button onClick={() => navigate('/video-templates')} className="text-left font-bold uppercase tracking-wider text-xs hover:text-secondary transition-colors w-fit flex items-center gap-1">
                                Video Maker <Sparkles size={12} className="fill-current" />
                            </button>
                            {!currentUser ? (
                                <button onClick={() => navigate('/login')} className="text-left font-bold uppercase tracking-wider text-xs hover:text-primary transition-colors w-fit">
                                    Login / Register
                                </button>
                            ) : (
                                <button onClick={() => navigate('/dashboard')} className="text-left font-bold uppercase tracking-wider text-xs hover:text-secondary transition-colors w-fit">
                                    Dashboard
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Social + CTA */}
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3">
                            <h3 className="font-black uppercase tracking-widest text-xs text-background/40">Connect</h3>
                            <div className="flex gap-2">
                                <a href="https://www.instagram.com/yo.akatsuki/" target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-background/20 hover:border-secondary hover:bg-secondary hover:text-secondary-foreground transition-all duration-200 group" aria-label="Instagram">
                                    <Instagram size={16} className="group-hover:scale-110 transition-transform" />
                                </a>
                                <a href="https://github.com/AadishY" target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-background/20 hover:border-accent hover:bg-accent hover:text-accent-foreground transition-all duration-200 group" aria-label="Github">
                                    <Github size={16} className="group-hover:scale-110 transition-transform" />
                                </a>
                            </div>
                        </div>

                        <button
                            onClick={() => currentUser ? navigate('/templates') : navigate('/login')}
                            className="flex items-center gap-2 bg-primary text-primary-foreground font-black uppercase py-2 px-4 text-xs tracking-wider border-2 border-background/20 hover:bg-primary/90 transition-colors group w-fit"
                        >
                            <Sparkles size={14} />
                            Create a Gift
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t-2 border-background/15 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 font-medium text-xs text-background/50">
                        Made with <Heart size={12} className="text-primary fill-primary animate-pulse" /> & Brutalism
                    </div>
                    <p className="text-[10px] sm:text-xs font-medium text-background/30 tracking-wider uppercase">
                        © {year} AadishLabs. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
}
